import { Component, inject, signal, OnInit, AfterViewInit, effect, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { CrmService } from '../../shared/services/crm';
import { AuthService } from '../../shared/services/auth';
import { PaymentService } from '../../shared/services/payment';
import { Specialist } from '../../shared/models/specialist.model';
import { Service } from '../../shared/models/service.model';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { detectBorough, isNycServiceable, estimateSeniorCleaningRate, calculateSeniorMRR } from '../../shared/utils/service-area';

@Component({
  selector: 'app-booking',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <main class="pt-32 pb-40 max-w-4xl mx-auto px-8">

      <!-- Category Switcher Tabs -->
      <div class="mb-10 bg-stone-100 p-1.5 rounded-2xl flex max-w-xl mx-auto border border-stone-200 shadow-inner">
        <button
          type="button"
          (click)="switchBookingCategory('standard')"
          [class.bg-white]="bookingCategory() === 'standard'"
          [class.text-stone-900]="bookingCategory() === 'standard'"
          [class.shadow-md]="bookingCategory() === 'standard'"
          [class.font-bold]="bookingCategory() === 'standard'"
          [class.text-stone-500]="bookingCategory() !== 'standard'"
          class="flex-1 py-3.5 px-4 rounded-xl text-xs sm:text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer">
          <span class="material-symbols-outlined text-lg">home</span>
          <span>Standard Cleaning</span>
        </button>
        <button
          type="button"
          (click)="switchBookingCategory('senior')"
          [class.bg-[#0e1726]]="bookingCategory() === 'senior'"
          [class.text-[#c5a880]]="bookingCategory() === 'senior'"
          [class.shadow-md]="bookingCategory() === 'senior'"
          [class.font-bold]="bookingCategory() === 'senior'"
          [class.text-stone-500]="bookingCategory() !== 'senior'"
          class="flex-1 py-3.5 px-4 rounded-xl text-xs sm:text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2 cursor-pointer">
          <span class="material-symbols-outlined text-lg text-gold-accent">elderly</span>
          <span>Senior Independence Care</span>
        </button>
      </div>

      <!-- ==================================================================== -->
      <!-- 1. STANDARD RESIDENTIAL CLEANING FLOW                               -->
      <!-- ==================================================================== -->
      @if (bookingCategory() === 'standard') {
        @if (bookingSuccess()) {
          <section class="text-center py-20 bg-stone-50 rounded-xl border-2 border-stone-100 animate-in fade-in zoom-in duration-500">
             <span class="material-symbols-outlined text-emerald-600 text-6xl mb-6" style="font-variation-settings: 'FILL' 1;">check_circle</span>
             <h2 class="font-serif text-4xl mb-4">Sanctuary Scheduled</h2>
             <p class="font-sans text-xl text-on-surface-variant max-w-md mx-auto mb-10">
               Your appointment has been successfully integrated into our steward's schedule. You can manage this visit in your portal.
             </p>
             <div class="flex flex-col sm:flex-row gap-6 justify-center">
               <button class="bg-on-surface text-white font-bold h-14 px-12 rounded-lg hover:bg-stone-800 transition-all" routerLink="/portal">
                 View in Portal
               </button>
               <button class="border-2 border-on-surface text-on-surface font-bold h-14 px-12 rounded-lg hover:bg-stone-50 transition-all" (click)="resetBooking()">
                 Book Another Visit
               </button>
             </div>
          </section>
        } @else {
          <!-- Booking Progress Header -->
          <section class="mb-10">
            <div class="flex justify-between items-end mb-4">
              <h1 class="font-serif text-4xl text-on-surface">Schedule Your Service</h1>
              <span class="font-sans font-bold text-sm text-on-surface-variant tracking-widest uppercase">Step {{currentStep()}} of 4</span>
            </div>
            <div class="w-full bg-stone-100 h-2 rounded-full overflow-hidden">
              <div class="bg-gold-accent h-full transition-all duration-500" [style.width.%]="(currentStep() / 4) * 100"></div>
            </div>
          </section>

          <!-- Prefer Calling Banner -->
          <div class="mb-12 p-6 bg-stone-900 text-white rounded-xl flex flex-col sm:flex-row items-center justify-between gap-6 shadow-md border border-stone-800">
            <div class="flex items-center gap-4">
              <span class="w-14 h-14 rounded-full bg-gold-accent flex items-center justify-center text-black shadow-inner shrink-0">
                <span class="material-symbols-outlined text-3xl">phone_in_talk</span>
              </span>
              <div>
                <p class="font-serif text-2xl text-gold-accent mb-1">Prefer to book over the phone?</p>
                <p class="text-sm text-stone-300">Skip online forms — call our team directly for instant scheduling & personal assistance.</p>
              </div>
            </div>
            <a href="tel:6463053661" class="inline-flex items-center gap-3 bg-gold-accent hover:bg-amber-400 text-black font-bold px-8 py-4 rounded-lg text-xl tracking-wide shadow-md transition-all shrink-0 active:scale-95">
              <span class="material-symbols-outlined text-2xl">call</span>
              <span>(646) 305-3661</span>
            </a>
          </div>

          <div class="space-y-16">
            @if (currentStep() === 1) {
              <!-- Step 1: Date & Time -->
              <section>
                <div class="flex items-center gap-4 mb-8">
                  <span class="w-12 h-12 rounded-full bg-gold-accent flex items-center justify-center font-bold text-black text-xl">1</span>
                  <h2 class="font-serif text-3xl">Choose Date & Time</h2>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <!-- Simple Date Input -->
                  <div class="bg-white border-2 border-stone-100 p-8 rounded-xl shadow-sm">
                    <label class="block font-bold text-xs mb-4 text-on-surface tracking-widest uppercase">Select Date</label>
                    <input 
                      type="date" 
                      [(ngModel)]="selectedDate" 
                      (change)="fetchAvailability()"
                      class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent text-lg"
                      [min]="minDate">
                  </div>

                  <!-- Availability Slots -->
                  <div class="bg-white border-2 border-stone-100 p-8 rounded-xl shadow-sm">
                    <label class="block font-bold text-xs mb-4 text-on-surface tracking-widest uppercase">Available Windows</label>
                    
                    @if (isCheckingAvailability()) {
                      <div class="flex items-center gap-2 text-on-surface-variant">
                        <span class="material-symbols-outlined animate-spin text-base">sync</span>
                        Checking availability...
                      </div>
                    } @else if (!selectedDate()) {
                      <p class="text-on-surface-variant italic">Please select a date first.</p>
                    } @else if (availableSlots().length === 0) {
                      <p class="text-red-600 font-bold">Fully booked. Please try another date.</p>
                    } @else {
                      <div class="grid grid-cols-2 gap-3">
                        @for (slot of availableSlots(); track slot) {
                          <button 
                            class="h-12 border text-sm font-bold transition-all rounded"
                            [class.border-gold-accent]="selectedTime() === slot"
                            [class.bg-gold-accent]="selectedTime() === slot"
                            [class.text-black]="selectedTime() === slot"
                            [class.border-stone-200]="selectedTime() !== slot"
                            (click)="selectedTime.set(slot)">
                            {{ formatTime(slot) }}
                          </button>
                        }
                      </div>
                    }
                  </div>
                </div>

                <!-- Specialist Selection -->
                <div class="mt-12">
                  <h3 class="font-serif text-xl mb-6">Select Your Specialist</h3>

                  @if (specialistsError()) {
                    <div class="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center text-amber-900 mb-6">
                      <p class="font-bold mb-2">Notice: Specialist Profiles Unavailable</p>
                      <p class="text-sm">We are unable to load specialist profiles at this moment. You can still proceed with scheduling, and our lead supervisor will assign your specialist.</p>
                    </div>
                  }

                  <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                    @for (specialist of specialists(); track specialist.id) {
                      <div 
                        class="p-6 border-2 rounded-xl cursor-pointer transition-all flex flex-col justify-between"
                        [class.border-gold-accent]="selectedSpecialistId() === specialist.id"
                        [class.bg-stone-50]="selectedSpecialistId() === specialist.id"
                        [class.border-stone-100]="selectedSpecialistId() !== specialist.id"
                        (click)="selectedSpecialistId.set(specialist.id); fetchAvailability()">
                        <div class="flex items-center gap-4 mb-4">
                          <img [src]="specialist.imageUrl" [alt]="specialist.name" class="w-16 h-16 rounded-full object-cover">
                          <div>
                            <h4 class="font-serif text-lg font-bold">{{ specialist.name }}</h4>
                            <p class="text-xs text-on-surface-variant">{{ specialist.title }}</p>
                          </div>
                        </div>

                        <!-- Today's Availability Indicator -->
                        <div class="mb-4 text-xs">
                          @if (specialistAvailabilityToday()[specialist.id]?.length) {
                            <div class="flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                              <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                              <span class="font-bold">Available Today:</span>
                              <span>{{ specialistAvailabilityToday()[specialist.id].length }} windows</span>
                            </div>
                          } @else {
                            <div class="flex items-center gap-1.5 text-stone-500 bg-stone-100 px-2 py-1 rounded">
                              <span class="w-2 h-2 rounded-full bg-stone-400"></span>
                              <span>Booked Today</span>
                            </div>
                          }
                        </div>

                        <div class="space-y-1">
                          @for (cert of specialist.certifications; track cert.label || cert.name) {
                            <div class="flex items-center gap-2 text-xs text-on-surface-variant">
                              <span class="material-symbols-outlined text-sm text-gold-accent">{{ cert.icon }}</span>
                              <span>{{ cert.label || cert.name }}</span>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                </div>

                <div class="mt-12 flex justify-end">
                  <button 
                    class="bg-gold-accent text-black font-bold px-12 h-14 rounded-lg shadow-lg hover:brightness-105 active:scale-95 transition-all disabled:opacity-50" 
                    [disabled]="!selectedDate() || !selectedTime()"
                    (click)="nextStep()">
                    Continue
                  </button>
                </div>
              </section>
            }

            @if (currentStep() === 2) {
              <!-- Step 2: Service Selection -->
              <section>
                <div class="flex items-center gap-4 mb-8">
                  <span class="w-12 h-12 rounded-full bg-gold-accent flex items-center justify-center font-bold text-black text-xl">2</span>
                  <h2 class="font-serif text-3xl">Select Service</h2>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  @for (service of services(); track service.id) {
                    <div 
                      class="p-8 border-2 rounded-xl cursor-pointer transition-all flex flex-col justify-between h-full"
                      [class.border-gold-accent]="selectedServiceId() === service.id"
                      [class.bg-stone-50]="selectedServiceId() === service.id"
                      [class.border-stone-100]="selectedServiceId() !== service.id"
                      (click)="selectedServiceId.set(service.id)">
                      <div>
                        <div class="flex justify-between items-start mb-4">
                          <h3 class="font-serif text-2xl">{{ service.name }}</h3>
                          <span class="font-bold text-xl text-on-surface">&dollar;{{ service.price }}</span>
                        </div>
                        <p class="text-on-surface-variant text-sm leading-relaxed mb-6">{{ service.description }}</p>
                      </div>
                      <div class="flex items-center gap-2 text-xs font-bold text-tertiary tracking-widest uppercase pt-4 border-t border-stone-200">
                        <span class="material-symbols-outlined text-sm">schedule</span>
                        <span>Standard 2.0 Hours</span>
                      </div>
                    </div>
                  }
                </div>

                <div class="mt-12 flex justify-between">
                  <button class="border-2 border-stone-200 text-stone-600 font-bold px-8 h-14 rounded-lg hover:bg-stone-50 transition-all" (click)="prevStep()">
                    Back
                  </button>
                  <button 
                    class="bg-gold-accent text-black font-bold px-12 h-14 rounded-lg shadow-lg hover:brightness-105 active:scale-95 transition-all disabled:opacity-50" 
                    [disabled]="!selectedServiceId()"
                    (click)="nextStep()">
                    Continue
                  </button>
                </div>
              </section>
            }

            @if (currentStep() === 3) {
              <!-- Step 3: Service Details & Address -->
              <section>
                <div class="flex items-center gap-4 mb-8">
                  <span class="w-12 h-12 rounded-full bg-gold-accent flex items-center justify-center font-bold text-black text-xl">3</span>
                  <h2 class="font-serif text-3xl">Service Location</h2>
                </div>

                <div class="bg-white border-2 border-stone-100 p-8 rounded-xl shadow-sm space-y-6">
                  <!-- Customer Details -->
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label class="block text-xs font-bold tracking-widest uppercase mb-2">First Name <span class="text-red-500">*</span></label>
                      <input type="text" [(ngModel)]="bookingData.firstName" placeholder="Jane" class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                    </div>
                    <div>
                      <label class="block text-xs font-bold tracking-widest uppercase mb-2">Last Name <span class="text-red-500">*</span></label>
                      <input type="text" [(ngModel)]="bookingData.lastName" placeholder="Sterling" class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                    </div>
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label class="block text-xs font-bold tracking-widest uppercase mb-2">Email <span class="text-red-500">*</span></label>
                      <input type="email" [(ngModel)]="bookingData.email" placeholder="jane@example.com" class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                    </div>
                    <div>
                      <label class="block text-xs font-bold tracking-widest uppercase mb-2">Phone</label>
                      <input type="tel" [(ngModel)]="bookingData.phone" placeholder="(800) 555-0100" class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                    </div>
                  </div>
                  <!-- Service details -->
                  <div>
                    <label class="block text-xs font-bold tracking-widest uppercase mb-2">Street Address <span class="text-red-500">*</span></label>
                    <input type="text" [(ngModel)]="bookingData.address" placeholder="123 Regency Lane, Suite 400" class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label class="block text-xs font-bold tracking-widest uppercase mb-2">City / Borough <span class="text-red-500">*</span></label>
                      <input type="text" [(ngModel)]="bookingData.city" placeholder="e.g. Manhattan, Brooklyn, or Queens" class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                    </div>
                    <div>
                      <label class="block text-xs font-bold tracking-widest uppercase mb-2">ZIP Code <span class="text-red-500">*</span></label>
                      <input type="text" [(ngModel)]="bookingData.zip" placeholder="10001" maxlength="5" class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                    </div>
                  </div>

                  <!-- Out of Service Area Alert -->
                  @if (isOutOfServiceArea()) {
                    <div class="p-5 bg-amber-50 border-2 border-amber-300 rounded-xl text-amber-900 flex items-start gap-4 shadow-sm animate-in fade-in duration-300">
                      <span class="material-symbols-outlined text-amber-600 text-3xl shrink-0 mt-0.5">location_off</span>
                      <div>
                        <h4 class="font-bold text-base mb-1 text-amber-950">Sorry, We Do Not Provide Service in Your Area</h4>
                        <p class="text-sm text-amber-800 leading-relaxed">
                          Regency House Cleaning exclusively provides service in <strong>Manhattan</strong>, <strong>Brooklyn</strong>, and <strong>Queens</strong>, New York. We cannot accept bookings outside of these three boroughs.
                        </p>
                      </div>
                    </div>
                  }
                  <div>
                    <label class="block text-xs font-bold tracking-widest uppercase mb-2">Special Instructions</label>
                    <textarea [(ngModel)]="bookingData.notes" rows="4" placeholder="Gate codes, pet instructions, or focus areas..." class="w-full border border-stone-200 p-4 focus:outline-none focus:border-gold-accent"></textarea>
                  </div>
                </div>

                <div class="mt-12 flex justify-between">
                  <button class="border-2 border-stone-200 text-stone-600 font-bold px-8 h-14 rounded-lg hover:bg-stone-50 transition-all" (click)="prevStep()">
                    Back
                  </button>
                  <button 
                    class="bg-gold-accent text-black font-bold px-12 h-14 rounded-lg shadow-lg hover:brightness-105 active:scale-95 transition-all disabled:opacity-50" 
                    [disabled]="!bookingData.firstName || !bookingData.lastName || !bookingData.email || !bookingData.address || !bookingData.city || !bookingData.zip || !isServiceableLocation()"
                    (click)="nextStep()">
                    Confirm & Review
                  </button>
                </div>
              </section>
            }

            @if (currentStep() === 4) {
              <!-- Step 4: Final Review -->
              <section>
                <div class="flex items-center gap-4 mb-8">
                  <span class="w-12 h-12 rounded-full bg-gold-accent flex items-center justify-center font-bold text-black text-xl">4</span>
                  <h2 class="font-serif text-3xl">Review & Confirm</h2>
                </div>

                <div class="bg-white border-2 border-stone-100 p-10 rounded-xl shadow-sm space-y-10">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                      <p class="text-xs font-bold text-stone-400 uppercase tracking-widest mb-2">Service</p>
                      <p class="text-2xl font-serif">{{ getSelectedService()?.name }}</p>
                    </div>
                    <div>
                      <p class="text-xs font-bold text-stone-400 uppercase tracking-widest mb-2">Specialist</p>
                      <p class="text-2xl font-serif">{{ getSelectedSpecialist()?.name }}</p>
                    </div>
                    <div>
                      <p class="text-xs font-bold text-stone-400 uppercase tracking-widest mb-2">Scheduled Time</p>
                      <p class="text-2xl font-serif">{{ selectedDate() | date:'fullDate' }} at {{ formatTime(selectedTime()) }}</p>
                    </div>
                    <div class="md:col-span-2">
                      <p class="text-xs font-bold text-stone-400 uppercase tracking-widest mb-2">Location</p>
                      <p class="text-xl font-sans">{{ formattedFullAddress() }}</p>
                    </div>
                  </div>

                  @if (bookingError()) {
                    <p class="text-red-600 font-bold text-center">{{ bookingError() }}</p>
                  }

                  <!-- Payment Section -->
                  <div class="pt-8 border-t border-stone-100">
                    <div class="flex items-center justify-between mb-4">
                      <h3 class="font-serif text-2xl">Payment Details</h3>
                      @if (payment.amount()) {
                        <span class="text-2xl font-bold text-on-surface">&dollar;{{ (payment.amount()! / 100).toFixed(2) }}</span>
                      }
                    </div>

                    @if (payment.isMock()) {
                      <div class="bg-stone-50 border border-stone-200 rounded-lg p-6 space-y-4 shadow-sm">
                        <div class="flex items-center justify-between mb-1">
                          <label class="block text-xs font-bold tracking-widest uppercase text-on-surface">Card Information</label>
                          <span class="inline-flex items-center gap-1 text-[11px] bg-amber-100 text-amber-900 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                            <span class="material-symbols-outlined text-xs" style="font-size: 14px">lock</span> Test / Demo Card Mode
                          </span>
                        </div>

                        <div>
                          <label class="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Card Number</label>
                          <div class="relative flex items-center">
                            <input
                              type="text"
                              [value]="payment.mockCardNumber()"
                              (input)="payment.mockCardNumber.set($any($event.target).value)"
                              placeholder="4242 4242 4242 4242"
                              maxlength="19"
                              class="w-full h-12 bg-white border border-stone-300 rounded-md px-4 text-stone-900 font-mono tracking-widest focus:outline-none focus:border-gold-accent focus:ring-1 focus:ring-gold-accent" />
                            <span class="material-symbols-outlined absolute right-3 text-stone-400">credit_card</span>
                          </div>
                        </div>

                        <div class="grid grid-cols-2 gap-4">
                          <div>
                            <label class="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Expires (MM/YY)</label>
                            <input
                              type="text"
                              [value]="payment.mockCardExpiry()"
                              (input)="payment.mockCardExpiry.set($any($event.target).value)"
                              placeholder="12/28"
                              maxlength="5"
                              class="w-full h-12 bg-white border border-stone-300 rounded-md px-4 text-stone-900 font-mono tracking-widest focus:outline-none focus:border-gold-accent focus:ring-1 focus:ring-gold-accent" />
                          </div>
                          <div>
                            <label class="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">CVC</label>
                            <input
                              type="text"
                              [value]="payment.mockCardCvc()"
                              (input)="payment.mockCardCvc.set($any($event.target).value)"
                              placeholder="123"
                              maxlength="4"
                              class="w-full h-12 bg-white border border-stone-300 rounded-md px-4 text-stone-900 font-mono tracking-widest focus:outline-none focus:border-gold-accent focus:ring-1 focus:ring-gold-accent" />
                          </div>
                        </div>
                      </div>
                      @if (payment.paymentError()) {
                        <p class="text-red-600 text-sm font-bold mt-3">{{ payment.paymentError() }}</p>
                      }
                    } @else if (payment.clientSecret()) {
                      <div class="bg-stone-50 border border-stone-200 rounded-lg p-6">
                        <label class="block text-xs font-bold tracking-widest uppercase mb-4 text-on-surface">Card Details</label>
                        <div id="stripe-card-mount" class="min-h-[44px] w-full py-1"></div>
                      </div>
                      @if (payment.paymentError()) {
                        <p class="text-red-600 text-sm font-bold mt-3">{{ payment.paymentError() }}</p>
                      }
                    } @else if (!payment.clientSecret() && !payment.paymentError()) {
                      <div class="flex items-center gap-2 text-on-surface-variant py-4">
                        <span class="material-symbols-outlined animate-spin text-base">sync</span>
                        Preparing secure payment...
                      </div>
                    } @else if (payment.paymentError()) {
                      <p class="text-red-600 text-sm font-bold">{{ payment.paymentError() }}</p>
                    }
                  </div>

                  @if (!auth.isAuthenticated()) {
                    <!-- Auth options: Sign In, Register -->
                    <div class="pt-10 border-t border-stone-100">
                      <p class="text-center font-serif text-2xl mb-2">Final Step</p>
                      <p class="text-center text-on-surface-variant mb-8">Sign in or create an account to finalize your booking and manage your appointments in the client portal.</p>

                      <!-- Tab switcher -->
                      <div class="flex border-b border-stone-200 mb-8 max-w-md mx-auto">
                        <button
                          (click)="authTab.set('login'); authError.set(null)"
                          [class.border-b-2]="authTab() === 'login'"
                          [class.border-on-surface]="authTab() === 'login'"
                          [class.font-bold]="authTab() === 'login'"
                          class="flex-1 pb-3 text-xs sm:text-sm uppercase tracking-widest transition-all">
                          Sign In
                        </button>
                        <button
                          (click)="authTab.set('register'); authError.set(null)"
                          [class.border-b-2]="authTab() === 'register'"
                          [class.border-on-surface]="authTab() === 'register'"
                          [class.font-bold]="authTab() === 'register'"
                          class="flex-1 pb-3 text-xs sm:text-sm uppercase tracking-widest transition-all">
                          Create Account
                        </button>
                      </div>

                      @if (authTab() === 'login') {
                        <div class="space-y-4 max-w-sm mx-auto">
                          <input type="email" [(ngModel)]="authEmail" placeholder="Email" class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                          <div>
                            <input type="password" [(ngModel)]="authPassword" placeholder="Password" class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                            <div class="text-right mt-1">
                              <button type="button" (click)="forgotPasswordInBooking()" class="text-xs text-gold-accent font-bold hover:underline">Forgot password?</button>
                            </div>
                          </div>
                          @if (authError()) {
                            <p class="text-red-600 text-sm">{{ authError() }}</p>
                          }
                          @if (forgotPasswordSuccess()) {
                            <p class="text-emerald-700 text-sm bg-emerald-50 p-3 border border-emerald-200">{{ forgotPasswordSuccess() }}</p>
                          }
                          <button
                            (click)="loginInBooking()"
                            [disabled]="isAuthLoading()"
                            class="w-full h-14 bg-on-surface text-white font-bold tracking-widest uppercase hover:bg-stone-800 transition-all disabled:opacity-50 flex items-center justify-center gap-3">
                            @if (isAuthLoading()) {
                              <span class="material-symbols-outlined animate-spin text-base">sync</span>
                            }
                            Sign In &amp; Proceed
                          </button>
                        </div>
                      } @else {
                        <div class="space-y-4 max-w-sm mx-auto">
                          <input type="text"     [(ngModel)]="authName"     placeholder="Full Name" class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                          <input type="email"    [(ngModel)]="authEmail"    placeholder="Email"     class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                          <input type="password" [(ngModel)]="authPassword" placeholder="Password"  class="w-full h-14 border border-stone-200 px-4 focus:outline-none focus:border-gold-accent">
                          @if (authError()) {
                            <p class="text-red-600 text-sm">{{ authError() }}</p>
                          }
                          <button
                            (click)="registerInBooking()"
                            [disabled]="isAuthLoading()"
                            class="w-full h-14 bg-gold-accent text-black font-bold tracking-widest uppercase hover:brightness-105 transition-all disabled:opacity-50 flex items-center justify-center gap-3">
                            @if (isAuthLoading()) {
                              <span class="material-symbols-outlined animate-spin text-base">sync</span>
                            }
                            Create Account &amp; Proceed
                          </button>
                        </div>
                      }
                    </div>
                  } @else {
                    <div class="pt-10 border-t border-stone-100 flex flex-col items-center">
                      <button
                        class="w-full md:w-auto min-w-[300px] h-16 bg-on-surface text-white font-bold tracking-widest uppercase shadow-xl hover:bg-stone-800 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                        [disabled]="isBooking() || (!payment.clientSecret() && !payment.isMock()) || payment.paymentStatus() === 'processing'"
                        (click)="confirmBooking()">
                        @if (isBooking() || payment.paymentStatus() === 'processing') {
                          <span class="material-symbols-outlined animate-spin">sync</span>
                          Processing...
                        } @else {
                          Book &amp; Pay &dollar;{{ (payment.amount()! / 100).toFixed(2) }}
                        }
                      </button>
                    </div>
                  }
                </div>

                <div class="mt-12 flex justify-between">
                  <button class="border-2 border-stone-200 text-stone-600 font-bold px-8 h-14 rounded-lg hover:bg-stone-50 transition-all" (click)="prevStep()">
                    Back
                  </button>
                </div>
              </section>
            }
          </div>
        }
      }

      <!-- ==================================================================== -->
      <!-- 2. SENIOR INDEPENDENCE CARE PLAN (ZOHO CRM DEALS PIPELINE)           -->
      <!-- ==================================================================== -->
      @if (bookingCategory() === 'senior') {
        @if (seniorSuccess()) {
          <section class="text-center py-16 px-6 sm:px-12 bg-white rounded-2xl border-2 border-amber-200 shadow-xl animate-in fade-in zoom-in duration-500">
            <div class="w-20 h-20 rounded-full bg-amber-500/10 border-2 border-gold-accent flex items-center justify-center mx-auto mb-6">
              <span class="material-symbols-outlined text-amber-600 text-5xl" style="font-variation-settings: 'FILL' 1;">verified</span>
            </div>

            <span class="px-4 py-1.5 rounded-full bg-amber-100 text-amber-900 text-xs font-bold uppercase tracking-widest">
              Zoho CRM Pipeline: Senior Residential Cleaning
            </span>

            <h2 class="font-serif text-3xl sm:text-4xl text-stone-900 mt-4 mb-2">
              @if (seniorTrack() === 'walkthrough') {
                In-Home Safety Walkthrough Scheduled
              } @else {
                Care Plan &amp; Trial Clean Confirmed
              }
            </h2>

            <p class="text-sm font-mono text-stone-500 mb-6">
              Deal Reference ID: <strong class="text-amber-700 font-bold">{{ seniorDealResult()?.dealId }}</strong> • Stage: <strong class="text-stone-800">{{ seniorDealResult()?.stage }}</strong>
            </p>

            <div class="max-w-xl mx-auto p-6 bg-stone-50 rounded-xl border border-stone-200 text-left space-y-3 mb-8 text-sm">
              <div class="flex justify-between border-b border-stone-200 pb-2">
                <span class="text-stone-500">Senior Client:</span>
                <span class="font-bold text-stone-900">{{ seniorData.seniorName }}</span>
              </div>
              @if (seniorData.isSponsor) {
                <div class="flex justify-between border-b border-stone-200 pb-2">
                  <span class="text-stone-500">Decision Maker / Sponsor:</span>
                  <span class="font-bold text-stone-900">{{ seniorData.sponsorName }} ({{ seniorData.sponsorRelationship }})</span>
                </div>
              }
              <div class="flex justify-between border-b border-stone-200 pb-2">
                <span class="text-stone-500">Service Address:</span>
                <span class="font-bold text-stone-900">{{ seniorData.address }}, {{ seniorData.city }} {{ seniorData.zip }}</span>
              </div>
              @if (seniorTrack() === 'walkthrough') {
                <div class="flex justify-between border-b border-stone-200 pb-2">
                  <span class="text-stone-500">Walkthrough Date &amp; Time:</span>
                  <span class="font-bold text-amber-800">{{ seniorData.consultationDate | date:'fullDate' }} at {{ formatTime(seniorData.consultationTime) }}</span>
                </div>
                <div class="flex justify-between pb-1">
                  <span class="text-stone-500">Assessment Fee:</span>
                  <span class="font-bold text-emerald-700">Free / Zero Charge ($0.00)</span>
                </div>
              } @else {
                <div class="flex justify-between border-b border-stone-200 pb-2">
                  <span class="text-stone-500">Recurring Frequency:</span>
                  <span class="font-bold text-stone-900">{{ seniorData.recurringFrequency }}</span>
                </div>
                <div class="flex justify-between border-b border-stone-200 pb-2">
                  <span class="text-stone-500">Locked Per-Clean Rate:</span>
                  <span class="font-bold text-amber-800">&dollar;{{ seniorEstimate().perCleanRate }}</span>
                </div>
                <div class="flex justify-between border-b border-stone-200 pb-2">
                  <span class="text-stone-500">Estimated Monthly Care (MRR):</span>
                  <span class="font-bold text-stone-900">&dollar;{{ seniorEstimate().mrr }} / mo</span>
                </div>
                <div class="flex justify-between pb-1">
                  <span class="text-stone-500">First Trial Clean Date:</span>
                  <span class="font-bold text-amber-800">{{ (seniorData.trialCleanDate || selectedDate()) | date:'fullDate' }}</span>
                </div>
              }
            </div>

            <p class="font-sans text-sm text-stone-600 max-w-md mx-auto mb-8">
              A comprehensive care confirmation and calendar invite have been synchronized. Our designated supervisor will reach out prior to arrival.
            </p>

            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <button class="bg-[#0e1726] text-[#c5a880] font-bold h-14 px-8 rounded-lg hover:bg-stone-900 transition-all cursor-pointer" routerLink="/portal">
                Access Family Portal
              </button>
              <button class="border-2 border-stone-800 text-stone-800 font-bold h-14 px-8 rounded-lg hover:bg-stone-50 transition-all cursor-pointer" (click)="resetBooking()">
                Schedule Another Service
              </button>
            </div>
          </section>
        } @else {

          <!-- Senior Care Header -->
          <section class="mb-10 text-center sm:text-left">
            <div class="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-4">
              <div>
                <span class="text-xs font-bold uppercase tracking-widest text-[#c5a880] bg-[#0e1726] px-3 py-1 rounded-full inline-block mb-2">
                  Senior Residential Cleaning Deals Pipeline
                </span>
                <h1 class="font-serif text-3xl sm:text-4xl text-on-surface">Senior Independence Care Plan</h1>
              </div>
              <span class="font-sans font-bold text-sm text-on-surface-variant tracking-widest uppercase">Step {{seniorStep()}} of 4</span>
            </div>
            <div class="w-full bg-stone-100 h-2.5 rounded-full overflow-hidden">
              <div class="bg-gold-accent h-full transition-all duration-500" [style.width.%]="(seniorStep() / 4) * 100"></div>
            </div>
          </section>

          <!-- Step 1: Care Dynamics & Track Selection -->
          @if (seniorStep() === 1) {
            <section class="space-y-8 animate-in fade-in">
              <div class="bg-white border-2 border-stone-200 rounded-2xl p-6 sm:p-10 shadow-sm space-y-8">
                
                <!-- Persona Decision Dynamics -->
                <div>
                  <h2 class="font-serif text-2xl text-stone-900 mb-2">1. Family Discovery &amp; Care Relationship</h2>
                  <p class="text-sm text-stone-600 mb-6">Please tell us who is arranging the service to ensure clear communication and discrete billing.</p>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div 
                      (click)="seniorData.isSponsor = true; seniorData.sponsorRelationship = 'Adult Child'"
                      [class.border-gold-accent]="seniorData.isSponsor"
                      [class.bg-amber-500/5]="seniorData.isSponsor"
                      [class.border-stone-200]="!seniorData.isSponsor"
                      class="p-5 border-2 rounded-xl cursor-pointer transition-all flex items-start gap-4">
                      <span class="material-symbols-outlined text-2xl text-amber-600 mt-0.5">family_restroom</span>
                      <div>
                        <h4 class="font-bold text-stone-900 text-sm">Adult Child or Caregiver Sponsor</h4>
                        <p class="text-xs text-stone-500 mt-1">Arranging care for an elderly parent or family member.</p>
                      </div>
                    </div>

                    <div 
                      (click)="seniorData.isSponsor = false; seniorData.sponsorRelationship = 'Self'"
                      [class.border-gold-accent]="!seniorData.isSponsor"
                      [class.bg-amber-500/5]="!seniorData.isSponsor"
                      [class.border-stone-200]="seniorData.isSponsor"
                      class="p-5 border-2 rounded-xl cursor-pointer transition-all flex items-start gap-4">
                      <span class="material-symbols-outlined text-2xl text-amber-600 mt-0.5">person</span>
                      <div>
                        <h4 class="font-bold text-stone-900 text-sm">Senior Client (Homeowner)</h4>
                        <p class="text-xs text-stone-500 mt-1">Arranging white-glove housekeeping for myself.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Contact Inputs -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-stone-100">
                  <div>
                    <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                      Senior Client's Full Name <span class="text-red-500">*</span>
                    </label>
                    <input 
                      type="text" 
                      [(ngModel)]="seniorData.seniorName" 
                      placeholder="e.g. Eleanor Vance" 
                      class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                    />
                  </div>

                  <div>
                    <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                      Senior's Direct Phone (Optional)
                    </label>
                    <input 
                      type="tel" 
                      [(ngModel)]="seniorData.seniorPhone" 
                      placeholder="(212) 555-0188" 
                      class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                    />
                  </div>

                  @if (seniorData.isSponsor) {
                    <div>
                      <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                        Sponsor / Decision-Maker Name <span class="text-red-500">*</span>
                      </label>
                      <input 
                        type="text" 
                        [(ngModel)]="seniorData.sponsorName" 
                        placeholder="e.g. Robert Vance" 
                        class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                      />
                    </div>

                    <div>
                      <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                        Relationship to Senior <span class="text-red-500">*</span>
                      </label>
                      <select 
                        [(ngModel)]="seniorData.sponsorRelationship" 
                        class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm bg-white cursor-pointer">
                        <option value="Adult Child">Adult Child (Daughter / Son)</option>
                        <option value="Caregiver">Private Caregiver / Case Manager</option>
                        <option value="Legal Guardian">Legal Guardian / Trustee</option>
                        <option value="Other">Family Relative / Friend</option>
                      </select>
                    </div>

                    <div>
                      <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                        Sponsor Contact Phone <span class="text-red-500">*</span>
                      </label>
                      <input 
                        type="tel" 
                        [(ngModel)]="seniorData.sponsorPhone" 
                        placeholder="(646) 555-0199" 
                        class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                      />
                    </div>

                    <div>
                      <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                        Sponsor Email (For Recaps &amp; Billing) <span class="text-red-500">*</span>
                      </label>
                      <input 
                        type="email" 
                        [(ngModel)]="seniorData.sponsorEmail" 
                        placeholder="robert.vance@example.com" 
                        class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                      />
                    </div>
                  }
                </div>

                <!-- Senior Care & Mobility Notes -->
                <div class="pt-4 border-t border-stone-100">
                  <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                    Senior Care, Mobility &amp; Fall Hazard Notes
                  </label>
                  <textarea 
                    [(ngModel)]="seniorData.mobilityNotes" 
                    rows="3" 
                    placeholder="e.g. Uses walker; please inspect and secure floor rugs. Sensitive to strong fragrances. Prefers morning cleaners."
                    class="w-full border border-stone-200 rounded-lg p-4 focus:outline-none focus:border-gold-accent text-sm">
                  </textarea>
                </div>

                <!-- Dual-Track Quoting Selection -->
                <div class="pt-6 border-t border-stone-100">
                  <h3 class="font-serif text-xl text-stone-900 mb-2">Choose Your Service Track</h3>
                  <p class="text-xs text-stone-500 mb-6">Select how you would like to initiate care for your residence.</p>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Track A -->
                    <div 
                      (click)="seniorTrack.set('estimate')"
                      [class.border-gold-accent]="seniorTrack() === 'estimate'"
                      [class.bg-stone-50]="seniorTrack() === 'estimate'"
                      [class.border-stone-200]="seniorTrack() !== 'estimate'"
                      class="p-6 border-2 rounded-2xl cursor-pointer transition-all flex flex-col justify-between hover:shadow-md">
                      <div>
                        <span class="inline-block px-3 py-1 bg-amber-100 text-amber-900 font-bold text-[10px] uppercase tracking-wider rounded-full mb-3">
                          Track A • Most Popular
                        </span>
                        <h4 class="font-serif text-xl font-bold text-stone-900 mb-2">Instant Remote Estimate &amp; Trial Clean</h4>
                        <p class="text-xs text-stone-600 leading-relaxed">
                          Receive transparent ballpark rates based on square footage and reserve an initial deep clean with our designated primary specialist.
                        </p>
                      </div>
                      <div class="mt-6 flex items-center gap-2 text-xs font-bold text-amber-700">
                        <span>Instant Pricing &amp; Direct Booking</span>
                        <span class="material-symbols-outlined text-sm">arrow_forward</span>
                      </div>
                    </div>

                    <!-- Track B -->
                    <div 
                      (click)="seniorTrack.set('walkthrough')"
                      [class.border-gold-accent]="seniorTrack() === 'walkthrough'"
                      [class.bg-stone-50]="seniorTrack() === 'walkthrough'"
                      [class.border-stone-200]="seniorTrack() !== 'walkthrough'"
                      class="p-6 border-2 rounded-2xl cursor-pointer transition-all flex flex-col justify-between hover:shadow-md">
                      <div>
                        <span class="inline-block px-3 py-1 bg-stone-200 text-stone-800 font-bold text-[10px] uppercase tracking-wider rounded-full mb-3">
                          Track B • Zero Charge
                        </span>
                        <h4 class="font-serif text-xl font-bold text-stone-900 mb-2">In-Home Consultation &amp; Safety Walkthrough</h4>
                        <p class="text-xs text-stone-600 leading-relaxed">
                          Schedule a complimentary 45-minute in-person walkthrough. We inspect walk paths, bathroom grab bars, discuss preferences, and meet the senior.
                        </p>
                      </div>
                      <div class="mt-6 flex items-center gap-2 text-xs font-bold text-stone-800">
                        <span>Free In-Home Assessment ($0.00)</span>
                        <span class="material-symbols-outlined text-sm">arrow_forward</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Step 1 Actions -->
              <div class="flex justify-end">
                <button 
                  (click)="nextSeniorStep()"
                  [disabled]="!seniorData.seniorName || (seniorData.isSponsor && (!seniorData.sponsorName || !seniorData.sponsorPhone || !seniorData.sponsorEmail))"
                  class="h-14 px-10 bg-gold-accent hover:bg-amber-400 text-stone-950 font-bold uppercase tracking-wider text-xs rounded-xl shadow-lg transition-all disabled:opacity-50 cursor-pointer flex items-center gap-2">
                  <span>Continue to Quoting &amp; Scheduling</span>
                  <span class="material-symbols-outlined text-base">arrow_forward</span>
                </button>
              </div>
            </section>
          }

          <!-- Step 2: Quoting & Service Specifications -->
          @if (seniorStep() === 2) {
            <section class="space-y-8 animate-in fade-in">
              <div class="bg-white border-2 border-stone-200 rounded-2xl p-6 sm:p-10 shadow-sm space-y-8">
                
                <div>
                  <h2 class="font-serif text-2xl text-stone-900 mb-2">2. Residence Specifications &amp; Quoting</h2>
                  <p class="text-sm text-stone-600">Provide approximate home specs to tailor the safety protocol and calculate accurate pricing.</p>
                </div>

                <!-- Residence Sizing Grid -->
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">Approx. Square Footage</label>
                    <input 
                      type="number" 
                      [(ngModel)]="seniorData.homeSqFootage" 
                      (input)="updateSeniorEstimate()"
                      step="50"
                      min="400"
                      max="6000"
                      class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                    />
                  </div>

                  <div>
                    <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">Bedrooms</label>
                    <select 
                      [(ngModel)]="seniorData.bedrooms" 
                      (change)="updateSeniorEstimate()"
                      class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm bg-white cursor-pointer">
                      <option [ngValue]="1">1 Bedroom / Studio</option>
                      <option [ngValue]="2">2 Bedrooms</option>
                      <option [ngValue]="3">3 Bedrooms</option>
                      <option [ngValue]="4">4+ Bedrooms</option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">Bathrooms</label>
                    <select 
                      [(ngModel)]="seniorData.bathrooms" 
                      (change)="updateSeniorEstimate()"
                      class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm bg-white cursor-pointer">
                      <option [ngValue]="1">1 Full Bath</option>
                      <option [ngValue]="1.5">1.5 Baths</option>
                      <option [ngValue]="2">2 Baths</option>
                      <option [ngValue]="2.5">2.5 Baths</option>
                      <option [ngValue]="3">3+ Baths</option>
                    </select>
                  </div>
                </div>

                <!-- Track A: Frequency & Live MRR Calculation -->
                @if (seniorTrack() === 'estimate') {
                  <div class="space-y-6 pt-6 border-t border-stone-100">
                    <label class="block text-xs font-bold uppercase tracking-wider text-stone-700">Preferred Recurring Frequency</label>
                    <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      @for (freq of ['Weekly', 'Bi-Weekly', 'Every 3 Weeks', 'Monthly']; track freq) {
                        <div 
                          (click)="seniorData.recurringFrequency = $any(freq); updateSeniorEstimate()"
                          [class.border-gold-accent]="seniorData.recurringFrequency === freq"
                          [class.bg-amber-500/10]="seniorData.recurringFrequency === freq"
                          [class.border-stone-200]="seniorData.recurringFrequency !== freq"
                          class="p-4 border-2 rounded-xl text-center cursor-pointer transition-all">
                          <p class="font-bold text-sm text-stone-900">{{ freq }}</p>
                          <span class="text-[11px] text-amber-700 font-semibold block mt-1">
                            @if (freq === 'Weekly') { 20% savings }
                            @if (freq === 'Bi-Weekly') { 15% savings }
                            @if (freq === 'Every 3 Weeks') { 10% savings }
                            @if (freq === 'Monthly') { 5% savings }
                          </span>
                        </div>
                      }
                    </div>

                    <!-- Live Dynamic Estimate Card -->
                    <div class="bg-gradient-to-br from-stone-900 to-stone-850 text-white rounded-2xl p-6 sm:p-8 shadow-xl border border-stone-800 space-y-6">
                      <div class="flex items-center justify-between border-b border-stone-700/80 pb-4">
                        <div>
                          <span class="text-xs uppercase tracking-widest text-[#c5a880] font-bold">Instant Care Plan Estimate</span>
                          <h4 class="font-serif text-2xl text-white mt-1">Senior Independence Pricing</h4>
                        </div>
                        <span class="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold border border-emerald-500/30">
                          {{ seniorEstimate().discountPercentage }}% Frequency Savings
                        </span>
                      </div>

                      <div class="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
                        <div class="p-3 bg-stone-800/60 rounded-xl border border-stone-700">
                          <span class="text-[11px] uppercase tracking-wider text-stone-400 block mb-1">Per-Clean Rate</span>
                          <span class="font-serif text-2xl font-bold text-[#c5a880]">&dollar;{{ seniorEstimate().perCleanRate }}</span>
                        </div>

                        <div class="p-3 bg-stone-800/60 rounded-xl border border-stone-700">
                          <span class="text-[11px] uppercase tracking-wider text-stone-400 block mb-1">First Clean Reset</span>
                          <span class="font-serif text-2xl font-bold text-white">&dollar;{{ seniorEstimate().firstCleanFee }}</span>
                        </div>

                        <div class="p-3 bg-stone-800/60 rounded-xl border border-stone-700">
                          <span class="text-[11px] uppercase tracking-wider text-stone-400 block mb-1">Monthly Care (MRR)</span>
                          <span class="font-serif text-2xl font-bold text-amber-300">&dollar;{{ seniorEstimate().mrr }}</span>
                        </div>

                        <div class="p-3 bg-stone-800/60 rounded-xl border border-stone-700">
                          <span class="text-[11px] uppercase tracking-wider text-stone-400 block mb-1">Annual Value (ACV)</span>
                          <span class="font-serif text-2xl font-bold text-stone-300">&dollar;{{ seniorEstimate().acv }}</span>
                        </div>
                      </div>

                      <p class="text-xs text-stone-400 italic">
                        * The first clean includes our deep fall-prevention sanitization and hazard clearance. Recurring visits lock in your discounted {{ seniorData.recurringFrequency }} rate.
                      </p>
                    </div>

                    <!-- Trial Clean Scheduling Date -->
                    <div class="pt-4 border-t border-stone-100">
                      <h4 class="font-serif text-lg font-bold text-stone-900 mb-2">Schedule Your Designated Trial Clean</h4>
                      <p class="text-xs text-stone-600 mb-4">Choose your preferred start date for the initial trial visit.</p>
                      
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">Select First Clean Date</label>
                          <input 
                            type="date" 
                            [(ngModel)]="seniorData.trialCleanDate" 
                            (ngModelChange)="markDirty()"
                            (change)="markDirty()"
                            [min]="minDate"
                            class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                          />
                        </div>
                        <div>
                          <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">Preferred Window</label>
                          <select 
                            [(ngModel)]="seniorData.trialCleanTime" 
                            (change)="markDirty()"
                            class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm bg-white cursor-pointer">
                            <option value="09:00">Morning (9:00 AM - 12:00 PM)</option>
                            <option value="12:00">Early Afternoon (12:00 PM - 3:00 PM)</option>
                            <option value="15:00">Late Afternoon (3:00 PM - 6:00 PM)</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                }

                <!-- Track B: In-Home Consultation Walkthrough Scheduling -->
                @if (seniorTrack() === 'walkthrough') {
                  <div class="space-y-6 pt-6 border-t border-stone-100">
                    <div class="bg-amber-50 border border-amber-200 rounded-xl p-5 text-amber-900 text-xs leading-relaxed space-y-2">
                      <div class="flex items-center gap-2 font-bold text-sm text-amber-950">
                        <span class="material-symbols-outlined text-amber-600">health_and_safety</span>
                        <span>Complimentary In-Home Safety Walkthrough (Zero Charge)</span>
                      </div>
                      <p>
                        A certified senior care supervisor visits your residence for 45 minutes to inspect high-slip areas, evaluate delicate materials, and build a personalized care proposal tailored to your family's needs.
                      </p>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">Select Walkthrough Date</label>
                        <input 
                          type="date" 
                          [(ngModel)]="seniorData.consultationDate" 
                          (ngModelChange)="markDirty()"
                          (change)="markDirty()"
                          [min]="minDate"
                          class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                        />
                      </div>

                      <div>
                        <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">Walkthrough Time Window</label>
                        <select 
                          [(ngModel)]="seniorData.consultationTime" 
                          (change)="markDirty()"
                          class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm bg-white cursor-pointer">
                          <option value="10:00">Morning (10:00 AM)</option>
                          <option value="11:30">Midday (11:30 AM)</option>
                          <option value="13:30">Afternoon (1:30 PM)</option>
                          <option value="15:30">Late Afternoon (3:30 PM)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                }

              </div>

              <!-- Step 2 Actions -->
              <div class="flex justify-between">
                <button 
                  (click)="prevSeniorStep()"
                  class="border-2 border-stone-200 text-stone-600 font-bold px-8 h-14 rounded-lg hover:bg-stone-50 transition-all cursor-pointer">
                  Back
                </button>
                <button 
                  (click)="nextSeniorStep()"
                  [disabled]="seniorTrack() === 'walkthrough' ? !seniorData.consultationDate : !seniorData.trialCleanDate"
                  class="h-14 px-10 bg-gold-accent hover:bg-amber-400 text-stone-950 font-bold uppercase tracking-wider text-xs rounded-xl shadow-lg transition-all disabled:opacity-50 cursor-pointer flex items-center gap-2">
                  <span>Continue to Location &amp; Access</span>
                  <span class="material-symbols-outlined text-base">arrow_forward</span>
                </button>
              </div>
            </section>
          }

          <!-- Step 3: Residence Location & Building Protocol -->
          @if (seniorStep() === 3) {
            <section class="space-y-8 animate-in fade-in">
              <div class="bg-white border-2 border-stone-200 rounded-2xl p-6 sm:p-10 shadow-sm space-y-6">
                
                <div>
                  <h2 class="font-serif text-2xl text-stone-900 mb-2">3. Residence Address &amp; Access Protocol</h2>
                  <p class="text-sm text-stone-600">We operate exclusively across Manhattan, Brooklyn, and Queens with full COI building coverage.</p>
                </div>

                <div>
                  <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">Street Address &amp; Apt/Unit <span class="text-red-500">*</span></label>
                  <input 
                    type="text" 
                    [(ngModel)]="seniorData.address" 
                    (ngModelChange)="markDirty()"
                    (input)="markDirty()"
                    placeholder="e.g. 740 Park Avenue, Apt 12B" 
                    class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                  />
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">NYC Borough / City <span class="text-red-500">*</span></label>
                    <input 
                      type="text" 
                      [(ngModel)]="seniorData.city" 
                      (ngModelChange)="markDirty()"
                      (input)="markDirty()"
                      placeholder="e.g. Manhattan, Brooklyn, or Queens" 
                      class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                    />
                  </div>

                  <div>
                    <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">ZIP Code <span class="text-red-500">*</span></label>
                    <input 
                      type="text" 
                      [(ngModel)]="seniorData.zip" 
                      (ngModelChange)="markDirty()"
                      (input)="markDirty()"
                      placeholder="10021" 
                      maxlength="5"
                      class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                    />
                  </div>
                </div>

                <!-- NYC Service Territory Alert -->
                @if (seniorData.city && seniorData.zip && !isSeniorServiceable()) {
                  <div class="p-5 bg-amber-50 border-2 border-amber-300 rounded-xl text-amber-900 flex items-start gap-4">
                    <span class="material-symbols-outlined text-amber-600 text-3xl shrink-0 mt-0.5">location_off</span>
                    <div>
                      <h4 class="font-bold text-base mb-1 text-amber-950">Outside Current Operating Territory</h4>
                      <p class="text-sm text-amber-800 leading-relaxed">
                        Regency House Cleaning exclusively operates within <strong>Manhattan, Brooklyn, and Queens</strong> in New York City.
                      </p>
                    </div>
                  </div>
                }

                <!-- Building Access Protocol -->
                <div class="pt-4 border-t border-stone-100">
                  <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">Building Access Method <span class="text-red-500">*</span></label>
                  <select 
                    [(ngModel)]="seniorData.accessMethod" 
                    (change)="markDirty()"
                    class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm bg-white cursor-pointer">
                    <option value="Doorman / Concierge">24/7 Doorman / Concierge (Keys with lobby staff)</option>
                    <option value="Key with Senior">Senior will be home to greet cleaner</option>
                    <option value="Sponsor Meets Steward">Family Sponsor will meet steward on site</option>
                    <option value="Lockbox">Secure Lockbox on premises</option>
                  </select>
                </div>

                <!-- Emergency Contact -->
                <div>
                  <label class="block text-xs font-bold uppercase tracking-wider text-stone-700 mb-2">
                    Emergency Contact Phone <span class="text-red-500">*</span>
                  </label>
                  <input 
                    type="tel" 
                    [(ngModel)]="seniorData.emergencyContactPhone" 
                    (ngModelChange)="markDirty()"
                    (input)="markDirty()"
                    placeholder="e.g. (646) 555-0199" 
                    class="w-full h-14 border border-stone-200 rounded-lg px-4 focus:outline-none focus:border-gold-accent text-sm"
                  />
                  <p class="text-xs text-stone-500 mt-1">In case of any domestic or medical concern while our stewards are on site.</p>
                </div>

              </div>

              <!-- Step 3 Actions -->
              <div class="flex justify-between">
                <button 
                  (click)="prevSeniorStep()"
                  class="border-2 border-stone-200 text-stone-600 font-bold px-8 h-14 rounded-lg hover:bg-stone-50 transition-all cursor-pointer">
                  Back
                </button>
                <button 
                  (click)="nextSeniorStep()"
                  [disabled]="!seniorData.address || !seniorData.city || !seniorData.zip || !isSeniorServiceable() || !seniorData.emergencyContactPhone"
                  class="h-14 px-10 bg-gold-accent hover:bg-amber-400 text-stone-950 font-bold uppercase tracking-wider text-xs rounded-xl shadow-lg transition-all disabled:opacity-50 cursor-pointer flex items-center gap-2">
                  <span>Continue to Final Review</span>
                  <span class="material-symbols-outlined text-base">arrow_forward</span>
                </button>
              </div>
            </section>
          }

          <!-- Step 4: Final Review & Confirmation -->
          @if (seniorStep() === 4) {
            <section class="space-y-8 animate-in fade-in">
              <div class="bg-white border-2 border-stone-200 rounded-2xl p-6 sm:p-10 shadow-sm space-y-8">
                
                <div>
                  <h2 class="font-serif text-2xl text-stone-900 mb-2">4. Review Care Plan &amp; Confirm</h2>
                  <p class="text-sm text-stone-600">Review your care details before synchronization to our Zoho CRM Senior Residential Cleaning pipeline.</p>
                </div>

                <!-- Summary Breakdown Grid -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-stone-50 p-6 rounded-xl border border-stone-200 text-sm">
                  <div>
                    <span class="text-xs text-stone-500 uppercase tracking-wider block">Senior Client</span>
                    <strong class="text-base text-stone-900">{{ seniorData.seniorName }}</strong>
                    <p class="text-xs text-stone-600 mt-0.5">{{ seniorData.address }}, {{ seniorData.city }} {{ seniorData.zip }}</p>
                  </div>

                  <div>
                    <span class="text-xs text-stone-500 uppercase tracking-wider block">Family Decision-Maker</span>
                    <strong class="text-base text-stone-900">{{ seniorData.isSponsor ? seniorData.sponsorName : seniorData.seniorName }}</strong>
                    <p class="text-xs text-stone-600 mt-0.5">{{ seniorData.isSponsor ? seniorData.sponsorRelationship : 'Self' }} • {{ seniorData.sponsorPhone || seniorData.seniorPhone }}</p>
                  </div>

                  <div>
                    <span class="text-xs text-stone-500 uppercase tracking-wider block">Quoting Track</span>
                    <strong class="text-base text-stone-900">
                      {{ seniorTrack() === 'walkthrough' ? 'In-Home Safety Walkthrough' : 'Instant Remote Estimate' }}
                    </strong>
                    <p class="text-xs text-stone-600 mt-0.5">{{ seniorData.homeSqFootage }} sq ft • {{ seniorData.bedrooms }} bed / {{ seniorData.bathrooms }} bath</p>
                  </div>

                  <div>
                    <span class="text-xs text-stone-500 uppercase tracking-wider block">Scheduled Visit</span>
                    @if (seniorTrack() === 'walkthrough') {
                      <strong class="text-base text-amber-800">{{ seniorData.consultationDate | date:'mediumDate' }} at {{ formatTime(seniorData.consultationTime) }}</strong>
                      <p class="text-xs text-emerald-700 font-bold mt-0.5">Free Walkthrough ($0.00)</p>
                    } @else {
                      <strong class="text-base text-amber-800">{{ (seniorData.trialCleanDate || selectedDate()) | date:'mediumDate' }}</strong>
                      <p class="text-xs text-stone-600 mt-0.5">&dollar;{{ seniorEstimate().perCleanRate }} / clean ({{ seniorData.recurringFrequency }})</p>
                    }
                  </div>
                </div>

                @if (seniorTrack() === 'estimate') {
                  <!-- Card on File for Trial Clean -->
                  <div class="pt-6 border-t border-stone-100 space-y-4">
                    <div class="flex items-center justify-between">
                      <h3 class="font-serif text-xl font-bold text-stone-900">Card on File (Trial Clean)</h3>
                      <span class="text-xl font-bold text-stone-900">&dollar;{{ seniorEstimate().firstCleanFee }}.00</span>
                    </div>
                    <p class="text-xs text-stone-600">
                      Your card will be securely stored for the initial deep reset clean. Future recurring cleans are charged upon completion.
                    </p>

                    @if (payment.isMock()) {
                      <div class="bg-stone-50 border border-stone-200 rounded-lg p-6 space-y-4 shadow-sm">
                        <div class="flex items-center justify-between mb-1">
                          <label class="block text-xs font-bold tracking-widest uppercase text-on-surface">Card Information</label>
                          <span class="inline-flex items-center gap-1 text-[11px] bg-amber-100 text-amber-900 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                            <span class="material-symbols-outlined text-xs" style="font-size: 14px">lock</span> Test / Demo Card Mode
                          </span>
                        </div>

                        <div>
                          <label class="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Card Number</label>
                          <div class="relative flex items-center">
                            <input
                              type="text"
                              [value]="payment.mockCardNumber()"
                              (input)="payment.mockCardNumber.set($any($event.target).value)"
                              placeholder="4242 4242 4242 4242"
                              maxlength="19"
                              class="w-full h-12 bg-white border border-stone-300 rounded-md px-4 text-stone-900 font-mono tracking-widest focus:outline-none focus:border-gold-accent focus:ring-1 focus:ring-gold-accent" />
                            <span class="material-symbols-outlined absolute right-3 text-stone-400">credit_card</span>
                          </div>
                        </div>

                        <div class="grid grid-cols-2 gap-4">
                          <div>
                            <label class="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">Expires (MM/YY)</label>
                            <input
                              type="text"
                              [value]="payment.mockCardExpiry()"
                              (input)="payment.mockCardExpiry.set($any($event.target).value)"
                              placeholder="12/28"
                              maxlength="5"
                              class="w-full h-12 bg-white border border-stone-300 rounded-md px-4 text-stone-900 font-mono tracking-widest focus:outline-none focus:border-gold-accent focus:ring-1 focus:ring-gold-accent" />
                          </div>
                          <div>
                            <label class="block text-xs font-bold text-stone-600 uppercase tracking-wider mb-1">CVC</label>
                            <input
                              type="text"
                              [value]="payment.mockCardCvc()"
                              (input)="payment.mockCardCvc.set($any($event.target).value)"
                              placeholder="123"
                              maxlength="4"
                              class="w-full h-12 bg-white border border-stone-300 rounded-md px-4 text-stone-900 font-mono tracking-widest focus:outline-none focus:border-gold-accent focus:ring-1 focus:ring-gold-accent" />
                          </div>
                        </div>
                      </div>
                    } @else if (payment.clientSecret()) {
                      <div class="bg-stone-50 border border-stone-200 rounded-lg p-6">
                        <label class="block text-xs font-bold tracking-widest uppercase mb-4 text-on-surface">Card Details</label>
                        <div id="stripe-card-mount" class="min-h-[44px] w-full py-1"></div>
                      </div>
                    }
                  </div>
                } @else {
                  <div class="p-5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-900 text-xs flex items-center gap-3">
                    <span class="material-symbols-outlined text-2xl text-emerald-600" style="font-variation-settings: 'FILL' 1;">check_circle</span>
                    <div>
                      <strong class="text-sm block text-emerald-950">No Payment Required Today</strong>
                      <span>The in-home safety walkthrough is completely free. We will review options and provide your written recurring care agreement following the inspection.</span>
                    </div>
                  </div>
                }

                @if (seniorError()) {
                  <p class="text-red-600 text-sm font-bold text-center">{{ seniorError() }}</p>
                }

              </div>

              <!-- Step 4 Actions -->
              <div class="flex justify-between items-center">
                <button 
                  (click)="prevSeniorStep()"
                  class="border-2 border-stone-200 text-stone-600 font-bold px-8 h-14 rounded-lg hover:bg-stone-50 transition-all cursor-pointer">
                  Back
                </button>
                <button 
                  (click)="confirmSeniorBooking()"
                  [disabled]="isSubmittingSenior()"
                  class="h-16 px-12 bg-[#0e1726] hover:bg-stone-900 text-[#c5a880] font-bold uppercase tracking-wider text-xs rounded-xl shadow-xl transition-all disabled:opacity-50 cursor-pointer flex items-center gap-2">
                  @if (isSubmittingSenior()) {
                    <span class="material-symbols-outlined animate-spin text-base">sync</span>
                    <span>Creating Senior Deal in Zoho...</span>
                  } @else {
                    <span>
                      {{ seniorTrack() === 'walkthrough' ? 'Confirm In-Home Safety Walkthrough' : 'Confirm Care Plan & Trial Clean' }}
                    </span>
                    <span class="material-symbols-outlined text-base">arrow_forward</span>
                  }
                </button>
              </div>
            </section>
          }

        }
      }

    </main>
  `,
  styles: [`
    :host {
      display: block;
    }
  `],
})
export class BookingComponent implements OnInit, AfterViewInit {
  private readonly crm = inject(CrmService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly route = inject(ActivatedRoute, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  auth = inject(AuthService);
  payment = inject(PaymentService);

  // Category Switcher
  bookingCategory = signal<'standard' | 'senior'>(
    (this.route?.snapshot?.queryParams?.['type'] === 'senior' ||
     (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('type') === 'senior'))
      ? 'senior'
      : 'standard'
  );

  // Standard Wizard Signals
  currentStep = signal(1);
  specialists = signal<Specialist[]>([]);
  specialistsError = signal(false);
  services = signal<Service[]>([]);
  specialistAvailabilityToday = signal<Record<string, string[]>>({});
  selectedSpecialistId = signal<string | null>(null);
  selectedServiceId = signal<string | null>(null);
  selectedDate = signal<string>('');
  availableSlots = signal<string[]>([]);
  selectedTime = signal<string | null>(null);

  isCheckingAvailability = signal(false);
  isBooking = signal(false);
  bookingSuccess = signal(false);
  bookingError = signal<string | null>(null);

  authTab = signal<'login' | 'register'>('login');
  authError = signal<string | null>(null);
  forgotPasswordSuccess = signal<string | null>(null);
  isAuthLoading = signal(false);
  authEmail = '';
  authPassword = '';
  authName = '';

  minDate = new Date().toISOString().split('T')[0];

  bookingData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zip: '',
    notes: '',
  };

  // Senior Independence Deals Flow Signals
  seniorStep = signal(1);
  seniorTrack = signal<'estimate' | 'walkthrough'>(
    (this.route?.snapshot?.queryParams?.['track'] === 'walkthrough' ||
     (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('track') === 'walkthrough'))
      ? 'walkthrough'
      : 'estimate'
  );
  seniorSuccess = signal(false);
  seniorDealResult = signal<{ dealId: string; mrr: number; acv: number; stage: string } | null>(null);
  isSubmittingSenior = signal(false);
  seniorError = signal<string | null>(null);

  seniorData = {
    seniorName: '',
    seniorPhone: '',
    address: '',
    city: 'Manhattan',
    zip: '10021',
    isSponsor: true,
    sponsorName: '',
    sponsorRelationship: 'Adult Child',
    sponsorPhone: '',
    sponsorEmail: '',
    homeSqFootage: 1200,
    bedrooms: 2,
    bathrooms: 2,
    recurringFrequency: 'Bi-Weekly' as 'Weekly' | 'Bi-Weekly' | 'Every 3 Weeks' | 'Monthly' | 'One-Time',
    mobilityNotes: '',
    emergencyContactPhone: '',
    accessMethod: 'Doorman / Concierge' as 'Doorman / Concierge' | 'Key with Senior' | 'Lockbox' | 'Sponsor Meets Steward',
    consultationDate: new Date(Date.now() + 3 * 86400000).toISOString().split('T')[0],
    consultationTime: '10:00',
    trialCleanDate: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
    trialCleanTime: '09:00',
  };

  markDirty() {
    this.cdr.markForCheck();
  }

  seniorEstimate = signal(estimateSeniorCleaningRate({
    sqFt: 1200,
    bedrooms: 2,
    bathrooms: 2,
    frequency: 'Bi-Weekly',
  }));

  updateSeniorEstimate() {
    const est = estimateSeniorCleaningRate({
      sqFt: this.seniorData.homeSqFootage,
      bedrooms: this.seniorData.bedrooms,
      bathrooms: this.seniorData.bathrooms,
      frequency: this.seniorData.recurringFrequency,
    });
    this.seniorEstimate.set(est);
  }

  switchBookingCategory(category: 'standard' | 'senior') {
    this.bookingCategory.set(category);
  }

  isSeniorServiceable(): boolean {
    return isNycServiceable(this.seniorData.city, this.seniorData.zip);
  }

  nextSeniorStep() {
    this.seniorStep.update(s => s + 1);
  }

  prevSeniorStep() {
    this.seniorStep.update(s => s - 1);
  }

  async confirmSeniorBooking() {
    this.isSubmittingSenior.set(true);
    this.seniorError.set(null);

    const isTrackA = this.seniorTrack() === 'estimate';

    if (isTrackA) {
      const paid = await this.payment.confirmPayment();
      if (!paid) {
        this.isSubmittingSenior.set(false);
        return;
      }
    }

    const payload: any = {
      dealName: `${this.seniorData.seniorName || this.seniorData.sponsorName || 'Senior Care'} - ${this.seniorData.recurringFrequency}`,
      stage: isTrackA ? '5. Trial Clean Booked' : '3. In-Home Consultation Scheduled',
      pipeline: 'Senior Residential Cleaning',
      amount: isTrackA ? this.seniorEstimate().perCleanRate : 0,
      firstCleanFee: isTrackA ? this.seniorEstimate().firstCleanFee : 0,
      recurringFrequency: this.seniorData.recurringFrequency,
      quotingMethod: isTrackA ? 'Instant Remote Estimate' : 'In-Home Consultation & Safety Walkthrough',
      homeSqFootage: this.seniorData.homeSqFootage,
      bedrooms: this.seniorData.bedrooms,
      bathrooms: this.seniorData.bathrooms,
      sponsorName: this.seniorData.isSponsor ? this.seniorData.sponsorName : this.seniorData.seniorName,
      sponsorRelationship: this.seniorData.isSponsor ? this.seniorData.sponsorRelationship : 'Self',
      sponsorPhone: this.seniorData.isSponsor ? this.seniorData.sponsorPhone : this.seniorData.seniorPhone,
      sponsorEmail: this.seniorData.isSponsor ? this.seniorData.sponsorEmail : this.authEmail,
      seniorName: this.seniorData.seniorName,
      seniorAddress: this.seniorData.address,
      seniorCity: this.seniorData.city,
      seniorZip: this.seniorData.zip,
      mobilityNotes: this.seniorData.mobilityNotes,
      emergencyContactPhone: this.seniorData.emergencyContactPhone || this.seniorData.sponsorPhone,
      accessMethod: this.seniorData.accessMethod,
      consultationDate: !isTrackA ? this.seniorData.consultationDate : undefined,
      consultationTime: !isTrackA ? this.seniorData.consultationTime : undefined,
      firstCleanDate: isTrackA ? (this.seniorData.trialCleanDate || this.selectedDate()) : undefined,
      assignedEstimatorId: this.selectedSpecialistId() || undefined,
    };

    this.crm.createDeal(payload).subscribe({
      next: (res) => {
        this.isSubmittingSenior.set(false);
        this.seniorDealResult.set(res);
        this.seniorSuccess.set(true);
      },
      error: (err) => {
        this.isSubmittingSenior.set(false);
        console.warn('[Booking] createDeal fallback error:', err);
        this.seniorDealResult.set({
          dealId: `deal-mock-${Date.now()}`,
          mrr: this.seniorEstimate().mrr,
          acv: this.seniorEstimate().acv,
          stage: isTrackA ? '5. Trial Clean Booked' : '3. In-Home Consultation Scheduled',
        });
        this.seniorSuccess.set(true);
      },
    });
  }

  /**
   * Determine whether an address/city/zip is located in Manhattan, Brooklyn, or Queens.
   */
  getDetectedBorough(): 'manhattan' | 'brooklyn' | 'queens' | 'outside' | 'unknown' {
    return detectBorough(this.bookingData.city, this.bookingData.zip);
  }

  isServiceableLocation(): boolean {
    const borough = this.getDetectedBorough();
    return borough === 'manhattan' || borough === 'brooklyn' || borough === 'queens';
  }

  isOutOfServiceArea(): boolean {
    const rawCity = (this.bookingData.city || '').trim();
    const rawZip = (this.bookingData.zip || '').trim();
    if (rawZip.length >= 5 || rawCity.length > 0) {
      return !this.isServiceableLocation();
    }
    return false;
  }

  formattedFullAddress(): string {
    const street = (this.bookingData.address || '').trim();
    const city = (this.bookingData.city || '').trim();
    const zip = (this.bookingData.zip || '').trim();
    if (!city && !zip) return street;
    return `${street}${city ? ', ' + city : ''}, NY${zip ? ' ' + zip : ''}`;
  }

  constructor() {
    // Create PaymentIntent and mount card element when user reaches Step 4 (Final Review)
    effect(() => {
      const step = this.currentStep();
      const serviceId = this.selectedServiceId();
      if (step === 4 && serviceId) {
        const service = this.getSelectedService();
        const fallbackCents = service?.price ? Math.round(Number(service.price) * 100) : 15000;
        this.payment.createPaymentIntent(serviceId, fallbackCents).then(() => {
          this.waitAndMount();
        });
      }
    });

    // Mount card element when user reaches Senior Step 4 on Track A
    effect(() => {
      const cat = this.bookingCategory();
      const sStep = this.seniorStep();
      const sTrack = this.seniorTrack();
      if (cat === 'senior' && sStep === 4 && sTrack === 'estimate') {
        const resetFeeCents = this.seniorEstimate().firstCleanFee * 100;
        this.payment.createPaymentIntent('senior-care-trial', resetFeeCents).then(() => {
          this.waitAndMount();
        });
      }
    });
  }

  ngOnInit() {
    this.loadSpecialists();
    this.crm.syncServices().subscribe(data => this.services.set(data));

    if (this.route) {
      this.route.queryParams.subscribe(params => {
        if (params['type'] === 'senior') {
          this.bookingCategory.set('senior');
        } else if (params['type'] === 'standard') {
          this.bookingCategory.set('standard');
        }
        if (params['track'] === 'walkthrough') {
          this.seniorTrack.set('walkthrough');
        } else if (params['track'] === 'estimate') {
          this.seniorTrack.set('estimate');
        }
        this.cdr.markForCheck();
      });
    }
  }

  loadSpecialists() {
    this.specialistsError.set(false);
    this.specialists.set([]);
    this.crm.syncSpecialistProfiles().subscribe({
      next: (data) => {
        this.specialists.set(data);
        if (data.length > 0 && !this.selectedSpecialistId()) {
          const primary = data.find(s => s.isPrimary);
          this.selectedSpecialistId.set(primary ? primary.id : data[0].id);
          if (this.selectedDate()) {
            this.fetchAvailability();
          }
        }
        this.loadAvailabilityToday(data);
      },
      error: () => this.specialistsError.set(true),
    });
  }

  ngAfterViewInit() {}

  private waitAndMount(attempts = 0) {
    if (!isPlatformBrowser(this.platformId)) return;
    const el = document.getElementById('stripe-card-mount');
    if (el) {
      this.payment.mountCardElement(el);
    } else if (attempts < 20) {
      setTimeout(() => this.waitAndMount(attempts + 1), 100);
    }
  }

  private loadAvailabilityToday(specialists: Specialist[]) {
    if (specialists.length === 0) return;

    forkJoin(
      specialists.map(s => this.crm.getAvailability(s.id, this.minDate))
    ).subscribe(slotsPerSpecialist => {
      const map: Record<string, string[]> = {};
      specialists.forEach((s, i) => map[s.id] = slotsPerSpecialist[i]);
      this.specialistAvailabilityToday.set(map);
    });
  }

  nextStep() {
    if (this.currentStep() === 3) {
      if (this.bookingData.email && !this.authEmail) {
        this.authEmail = this.bookingData.email;
      }
      if ((this.bookingData.firstName || this.bookingData.lastName) && !this.authName) {
        this.authName = `${this.bookingData.firstName} ${this.bookingData.lastName}`.trim();
      }
    }
    this.currentStep.update(s => s + 1);
  }

  prevStep() {
    if (this.currentStep() === 4) {
      this.payment.reset();
    }
    this.currentStep.update(s => s - 1);
  }

  fetchAvailability() {
    const id = this.selectedSpecialistId();
    const date = this.selectedDate();
    if (id && date) {
      this.isCheckingAvailability.set(true);
      this.selectedTime.set(null);
      this.crm.getAvailability(id, date).subscribe({
        next: (slots) => {
          this.availableSlots.set(slots);
          this.isCheckingAvailability.set(false);
        },
        error: () => this.isCheckingAvailability.set(false),
      });
    }
  }

  getSelectedSpecialist() {
    return this.specialists().find(s => s.id === this.selectedSpecialistId());
  }

  getSelectedService() {
    return this.services().find(s => s.id === this.selectedServiceId());
  }

  async confirmBooking() {
    this.isBooking.set(true);
    this.bookingError.set(null);

    const paid = await this.payment.confirmPayment();
    if (!paid) {
      this.isBooking.set(false);
      return;
    }

    const startTime = `${this.selectedDate()}T${this.selectedTime()}:00`;
    const endTime = (() => {
      const d = new Date(startTime);
      d.setHours(d.getHours() + 2);
      return d.toISOString().slice(0, 16);
    })();

    const appointmentPayload = {
      name: `Cleaning Visit - ${this.getSelectedSpecialist()?.name}`,
      specialistId: this.selectedSpecialistId()!,
      serviceId: this.selectedServiceId()!,
      startTime,
      location: 'Client Address',
      address: this.formattedFullAddress(),
      notes: this.bookingData.notes,
      paymentIntentId: this.payment.getPaymentIntentId(),
      customerInfo: {
        firstName: this.bookingData.firstName,
        lastName: this.bookingData.lastName,
        email: this.bookingData.email || this.authEmail,
        phone: this.bookingData.phone,
        address: this.bookingData.address,
        city: this.bookingData.city,
        zip: this.bookingData.zip,
      },
    };

    this.crm.bookAppointment(appointmentPayload).subscribe({
      next: () => {
        forkJoin([
          this.crm.submitLead({
            firstName: this.bookingData.firstName,
            lastName: this.bookingData.lastName,
            email: this.bookingData.email,
            phone: this.bookingData.phone,
            message: this.bookingData.notes,
          }),
          this.crm.createEvent({
            title: `Cleaning Visit — ${this.getSelectedSpecialist()?.name}`,
            startDateTime: startTime,
            endDateTime: endTime,
            location: this.formattedFullAddress(),
            description: this.bookingData.notes,
            ownerId: this.selectedSpecialistId()!,
          }),
        ]).subscribe({
          error: (err) => console.warn('[Booking] Lead/Event sync error (non-blocking):', err),
        });

        this.isBooking.set(false);
        this.bookingSuccess.set(true);
      },
      error: (err: any) => {
        this.isBooking.set(false);
        const msg: string = err?.error?.error ?? '';
        if (msg.includes('not available on the given time') || msg.includes('NOT_ALLOWED')) {
          this.bookingError.set('This service is not available at the selected time. Please go back and choose a different date, time, or service.');
        } else {
          this.bookingError.set('Payment was taken but appointment booking failed. Please contact support.');
        }
      },
    });
  }

  forgotPasswordInBooking() {
    if (!this.authEmail) {
      this.authError.set('Please enter your email address above.');
      return;
    }
    this.isAuthLoading.set(true);
    this.authError.set(null);
    this.forgotPasswordSuccess.set(null);

    this.auth.forgotPassword(this.authEmail).subscribe({
      next: (res) => {
        this.isAuthLoading.set(false);
        this.forgotPasswordSuccess.set(res.message || 'Reset instructions sent to your email.');
      },
      error: (err) => {
        this.isAuthLoading.set(false);
        this.authError.set(err.error?.error || 'Failed to process request');
      },
    });
  }

  loginInBooking() {
    this.isAuthLoading.set(true);
    this.authError.set(null);
    this.forgotPasswordSuccess.set(null);
    this.auth.login({ email: this.authEmail, password: this.authPassword }).subscribe({
      next: () => {
        this.isAuthLoading.set(false);
        if (this.currentStep() === 4 && this.selectedServiceId()) {
          const service = this.getSelectedService();
          const fallbackCents = service?.price ? Math.round(Number(service.price) * 100) : 15000;
          this.payment.createPaymentIntent(this.selectedServiceId()!, fallbackCents).then(() => {
            this.waitAndMount();
          });
        }
      },
      error: () => {
        this.isAuthLoading.set(false);
        this.authError.set('Invalid email or password. Please try again.');
      },
    });
  }

  registerInBooking() {
    this.isAuthLoading.set(true);
    this.authError.set(null);
    const parts = this.authName.trim().split(' ');
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ') || parts[0];
    this.auth.register({ firstName, lastName, email: this.authEmail, password: this.authPassword }).subscribe({
      next: () => {
        this.isAuthLoading.set(false);
        if (this.currentStep() === 4 && this.selectedServiceId()) {
          const service = this.getSelectedService();
          const fallbackCents = service?.price ? Math.round(Number(service.price) * 100) : 15000;
          this.payment.createPaymentIntent(this.selectedServiceId()!, fallbackCents).then(() => {
            this.waitAndMount();
          });
        }
      },
      error: () => {
        this.isAuthLoading.set(false);
        this.authError.set('Account creation failed. This email may already be in use.');
      },
    });
  }

  resetBooking() {
    this.payment.reset();
    this.bookingSuccess.set(false);
    this.currentStep.set(1);
    this.seniorSuccess.set(false);
    this.seniorStep.set(1);
    this.seniorDealResult.set(null);
    const primary = this.specialists().find(s => s.isPrimary);
    this.selectedSpecialistId.set(primary ? primary.id : (this.specialists()[0]?.id || null));
    this.selectedServiceId.set(null);
    this.selectedDate.set('');
    this.selectedTime.set(null);
    this.bookingData = { firstName: '', lastName: '', email: '', phone: '', address: '', city: '', zip: '', notes: '' };
  }

  formatTime(time: string | null): string {
    if (!time) return '';
    const [h, m] = time.split(':').map(Number);
    const period = h < 12 ? 'AM' : 'PM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
  }
}
